// components
import EnterprisePortalPage from '@karehero/llama/components/pages/EnterprisePortal/EnterprisePortal';
import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

// store
import {
  useAddInviteKeysToOrganizationMutation,
  useGetOrganizationAccountsQuery,
  useGetSessionOrganizationQuery,
  useInviteMembersToOrganizationMutation,
  useReinviteMemberMutation,
  useResendEmailInvitesToAllMembersMutation,
} from 'store/api/organization';
import { useSelector } from 'store';
import { newToast } from 'store/slices/uiSlice';

import { useDispatch } from 'react-redux';
import { useCallback } from 'react';

// utils
import { reportInviteError } from 'utils/inviteError';

// hooks
import { isIFAOrganizationTypeID } from '@karehero/models';

/**
 * EnterprisePortal shows the accounts and invited members of an organization
 */
export const EnterprisePortal = () => {
  // hooks
  const dispatch = useDispatch();
  const [reinviteMember] = useReinviteMemberMutation();
  const [inviteMembers] = useInviteMembersToOrganizationMutation();
  const [addInviteKeys] = useAddInviteKeysToOrganizationMutation();
  const { data: organization } = useGetSessionOrganizationQuery();
  const { data: accounts } = useGetOrganizationAccountsQuery(
    { organizationID: organization?.id || '', role: 'member' },
    { skip: !organization?.id },
  );
  const [resendEmailInvitesToAllMembers] =
    useResendEmailInvitesToAllMembersMutation();
  const account = useSelector((state) => state.iam.account);

  const isAdmin = account?.accountRole.name === 'KareHero Administrator';

  // methods
  const handleResendInvite = useCallback(
    async (id?: string, email?: string) => {
      if (!id) return;
      try {
        await reinviteMember(id).unwrap();
        dispatch(
          newToast({
            title: 'Invitation Success',
            description: `Resent invitation to ${email || 'the user'}`,
          }),
        );
      } catch (e) {
        reportInviteError(e);
      }
    },
    [dispatch, reinviteMember],
  );

  const handleResendAllEmail = useCallback(async () => {
    const res = await resendEmailInvitesToAllMembers(organization?.id || '');

    if ('error' in res) {
      console.error('Error resending email invites:', res.error);
      dispatch(
        newToast({
          title: 'Error',
          description: 'Failed to resend email invites.',
        }),
      );
      return;
    }

    dispatch(
      newToast({
        title: 'Success',
        description: 'Email invites resent successfully.',
      }),
    );
  }, [resendEmailInvitesToAllMembers, organization?.id, dispatch]);

  const isIFA = isIFAOrganizationTypeID(organization?.organizationTypeId);

  return (
    <PageTemplate
      maxWidth='xl'
      headerSlot={
        <PageHeader title={`${organization?.name || ''} Invite Portal`} />
      }
      contentSlot={
        <EnterprisePortalPage
          accounts={accounts}
          resendEmailInvites={handleResendAllEmail}
          organization={organization}
          inviteMembers={inviteMembers}
          resendInvite={handleResendInvite}
          addInviteKeys={addInviteKeys}
          notifyToast={(req: { title: string; description: string }) =>
            dispatch(newToast(req))
          }
          isAdmin={isAdmin}
          isTitleHidden
        />
      }
    />
  );
};

export default EnterprisePortal;
